<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { BubbleMenu } from '@tiptap/vue-3/menus';
import { Extension } from '@tiptap/core';
import DragHandle from '@tiptap/extension-drag-handle';
import Suggestion, { exitSuggestion } from '@tiptap/suggestion';
import TextAlign from '@tiptap/extension-text-align';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import {
    AlignCenter,
    AlignJustify,
    AlignLeft,
    AlignRight,
    Bold,
    ChevronDown,
    Heading1,
    Heading2,
    Heading3,
    Italic,
    Link2,
    List,
    ListOrdered,
    Quote,
    Redo2,
    RotateCcw,
    Underline as UnderlineIcon,
} from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ContractFieldNode, ContractSignatureNode, ContractTableNode } from './ContractFieldNode';
import EditorSlashMenu, { type EditorSlashItem } from './EditorSlashMenu.vue';
import { EditorColumn, EditorColumns } from './EditorColumns';

interface Props {
    modelValue: string;
    placeholder?: string;
    density?: 'default' | 'compact';
    helperText?: string;
    contractMode?: boolean;
    showToolbar?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    placeholder: 'Start writing…',
    density: 'default',
    helperText: 'Capture discovery notes, decisions, and handoff details.',
    contractMode: false,
    showToolbar: false,
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
    (e: 'blur'): void;
    (e: 'pasteFiles', files: File[]): void;
}>();

const textStyleOptions = [
    { label: 'Normal', value: 'paragraph' },
    { label: 'Heading 1', value: 'heading-1' },
    { label: 'Heading 2', value: 'heading-2' },
    { label: 'Heading 3', value: 'heading-3' },
    { label: 'Quote', value: 'blockquote' },
] as const;

const linkEditorOpen = ref(false);
const linkDraft = ref('');
const linkInput = ref<HTMLInputElement | null>(null);
const tableSelectionActive = ref(false);
const slashOpen = ref(false);
const slashItems = ref<EditorSlashItem[]>([]);
const slashSelectedIndex = ref(0);
const slashRange = ref<{ from: number; to: number } | null>(null);
const slashLeft = ref(0);
const slashTop = ref(0);
const blockMenuOpen = ref(false);
const blockMenuLeft = ref(0);
const blockMenuTop = ref(0);
const blockPosition = ref<number | null>(null);
const hoveredHandlePosition = ref<number | null>(null);
let dragHandleElement: HTMLElement | null = null;
const activeBlockMenuItems = ref<EditorSlashItem[]>([]);
const blockMenuTitle = ref('Block actions');
const columnMenuItems: EditorSlashItem[] = [
    { id: 'move-column-left', label: 'Move column left', description: 'Swap with the previous column', keywords: ['reorder'] },
    { id: 'move-column-right', label: 'Move column right', description: 'Swap with the next column', keywords: ['reorder'] },
];
const imageEditorOpen = ref(false);
const imageDraft = ref('');
const blockMenuItems: EditorSlashItem[] = [
    { id: 'paragraph', label: 'Turn into text', description: 'Plain paragraph', keywords: [] },
    { id: 'heading-1', label: 'Turn into heading 1', description: 'Large heading', keywords: [] },
    { id: 'heading-2', label: 'Turn into heading 2', description: 'Medium heading', keywords: [] },
    { id: 'heading-3', label: 'Turn into heading 3', description: 'Small heading', keywords: [] },
    { id: 'bullet-list', label: 'Turn into bulleted list', description: 'Bulleted items', keywords: [] },
    { id: 'ordered-list', label: 'Turn into numbered list', description: 'Numbered items', keywords: [] },
    { id: 'quote', label: 'Turn into quote', description: 'Emphasised passage', keywords: [] },
    { id: 'image', label: 'Add image', description: 'Insert an image by URL', keywords: ['photo', 'picture'] },
    { id: 'table', label: 'Add table', description: 'Insert a 3 × 3 table', keywords: ['grid'] },
    { id: 'duplicate', label: 'Duplicate block', description: 'Make a copy below', keywords: [] },
    { id: 'delete', label: 'Delete block', description: 'Remove this block', keywords: [] },
];

const blockItems: EditorSlashItem[] = [
    { id: 'paragraph', label: 'Text', description: 'A plain paragraph', keywords: ['text', 'paragraph', 'normal'] },
    { id: 'heading-1', label: 'Heading 1', description: 'Large section heading', keywords: ['heading', 'title', 'h1'] },
    { id: 'heading-2', label: 'Heading 2', description: 'Medium section heading', keywords: ['heading', 'subtitle', 'h2'] },
    { id: 'heading-3', label: 'Heading 3', description: 'Small section heading', keywords: ['heading', 'h3'] },
    { id: 'bullet-list', label: 'Bulleted list', description: 'List items with bullets', keywords: ['bullet', 'list', 'unordered'] },
    { id: 'ordered-list', label: 'Numbered list', description: 'List items with numbers', keywords: ['numbered', 'list', 'ordered'] },
    { id: 'quote', label: 'Quote', description: 'Emphasise a passage', keywords: ['quote', 'blockquote'] },
    { id: 'divider', label: 'Divider', description: 'Separate sections', keywords: ['divider', 'line', 'rule'] },
    { id: 'code', label: 'Code block', description: 'Monospaced text', keywords: ['code', 'preformatted'] },
    { id: 'columns-2', label: 'Two columns', description: 'Side-by-side content', keywords: ['columns', 'layout', 'two'] },
    { id: 'columns-3', label: 'Three columns', description: 'A three-part layout', keywords: ['columns', 'layout', 'three'] },
    { id: 'image', label: 'Image', description: 'Insert an image by URL', keywords: ['photo', 'picture'] },
    { id: 'table', label: 'Table', description: 'A grid of editable cells', keywords: ['grid', 'rows', 'columns'] },
];

function availableBlockItems(): EditorSlashItem[] {
    return props.contractMode ? [
        ...blockItems,
        { id: 'tenant-signature', label: 'Tenant signature', description: 'Place the tenant signing position', keywords: ['tenant', 'signature', 'sign'] },
        { id: 'client-signature', label: 'Client signature', description: 'Place the client signing position', keywords: ['client', 'signature', 'sign'] },
    ] : blockItems;
}

function positionSlashMenu(rect?: DOMRect | null) {
    if (!rect || typeof window === 'undefined') return;
    slashLeft.value = Math.max(8, Math.min(rect.left, window.innerWidth - 296));
    slashTop.value = rect.bottom + 328 > window.innerHeight ? Math.max(8, rect.top - 328) : rect.bottom + 8;
}

function updateSlashMenu(suggestion: any) {
    slashOpen.value = true;
    slashItems.value = suggestion.items || [];
    slashRange.value = suggestion.range;
    slashSelectedIndex.value = 0;
    positionSlashMenu(suggestion.clientRect?.());
}

function runSlashCommand(id: string) {
    const currentEditor = editor.value;
    const range = slashRange.value;
    if (!currentEditor || !range) return;
    const chain = currentEditor.chain().focus().deleteRange(range);
    if (id === 'paragraph') chain.setParagraph().run();
    else if (id === 'heading-1') chain.setHeading({ level: 1 }).run();
    else if (id === 'heading-2') chain.setHeading({ level: 2 }).run();
    else if (id === 'heading-3') chain.setHeading({ level: 3 }).run();
    else if (id === 'bullet-list') chain.toggleBulletList().run();
    else if (id === 'ordered-list') chain.toggleOrderedList().run();
    else if (id === 'quote') chain.toggleBlockquote().run();
    else if (id === 'divider') chain.setHorizontalRule().run();
    else if (id === 'code') chain.toggleCodeBlock().run();
    else if (id === 'image') { chain.run(); imageDraft.value = ''; imageEditorOpen.value = true; }
    else if (id === 'table') chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    else if (id === 'columns-2' || id === 'columns-3') {
        const count = id === 'columns-2' ? 2 : 3;
        chain.insertContent({ type: 'editorColumns', attrs: { count }, content: Array.from({ length: count }, () => ({ type: 'editorColumn', content: [{ type: 'paragraph' }] })) }).run();
    }
    else if (id === 'tenant-signature' && props.contractMode) chain.insertContent({ type: 'contractSignature', attrs: { role: 'tenant' } }).run();
    else if (id === 'client-signature' && props.contractMode) chain.insertContent({ type: 'contractSignature', attrs: { role: 'client' } }).run();
    slashOpen.value = false;
    slashRange.value = null;
}

const slashExtension = Extension.create({
    name: 'vueySlashCommands',
    addProseMirrorPlugins() {
        return [Suggestion({
            editor: this.editor,
            char: '/',
            startOfLine: true,
            allowSpaces: true,
            items: ({ query }) => {
                const term = query.trim().toLocaleLowerCase();
                return availableBlockItems().filter(item => !term || `${item.label} ${item.keywords.join(' ')}`.toLocaleLowerCase().includes(term));
            },
            command: ({ props: item }) => runSlashCommand(item.id),
            render: () => ({
                onStart: updateSlashMenu,
                onUpdate: updateSlashMenu,
                onKeyDown: ({ event }) => {
                    if (event.key === 'Escape') { slashOpen.value = false; if (editor.value) exitSuggestion(editor.value.view); return true; }
                    if (event.key === 'ArrowDown') { event.preventDefault(); slashSelectedIndex.value = (slashSelectedIndex.value + 1) % Math.max(1, slashItems.value.length); return true; }
                    if (event.key === 'ArrowUp') { event.preventDefault(); slashSelectedIndex.value = (slashSelectedIndex.value - 1 + Math.max(1, slashItems.value.length)) % Math.max(1, slashItems.value.length); return true; }
                    if (event.key === 'Enter' && slashItems.value.length) { event.preventDefault(); runSlashCommand(slashItems.value[slashSelectedIndex.value]?.id || slashItems.value[0]!.id); return true; }
                    return false;
                },
                onExit: () => { slashOpen.value = false; slashRange.value = null; },
            }),
        })];
    },
});

const editor = useEditor({
    content: normalizeIncomingHtml(props.modelValue),
    immediatelyRender: false,
    extensions: [
        StarterKit.configure({
            heading: {
                levels: [1, 2, 3],
            },
            link: false,
            underline: false,
            dropcursor: { color: 'color-mix(in srgb, var(--primary) 28%, transparent)', width: 1 },
        }),
        Underline,
        TextAlign.configure({
            types: ['heading', 'paragraph'],
            alignments: ['left', 'center', 'right', 'justify'],
            defaultAlignment: 'left',
        }),
        Link.configure({
            openOnClick: false,
            autolink: true,
            protocols: ['http', 'https', 'mailto'],
            defaultProtocol: 'https',
        }),
        Placeholder.configure({
            placeholder: ({ node }) => node.type.name === 'paragraph' ? 'Start typing or use / for blocks' : '',
            emptyEditorClass: 'is-editor-empty',
            showOnlyCurrent: true,
            includeChildren: true,
        }),
        slashExtension,
        Image.configure({ inline: false, allowBase64: false }),
        TableKit.configure({
            table: {
                resizable: true,
                renderWrapper: true,
                cellMinWidth: 80,
            },
        }),
        EditorColumn,
        EditorColumns,
        DragHandle.configure({
            nested: {
                edgeDetection: 'none',
                defaultRules: false,
                rules: [
                    {
                        id: 'skip-structural-nodes',
                        evaluate: ({ node, parent, isFirst }) => {
                            if (node.isInline || node.isText || ['tableRow', 'tableCell', 'tableHeader'].includes(node.type.name)) return 1000;
                            if (parent?.type.name === 'editorColumn') return 1000;
                            if (isFirst && parent && ['listItem', 'taskItem'].includes(parent.type.name)) return 1000;
                            if (node.firstChild && ['listItem', 'taskItem'].includes(node.firstChild.type.name)) return 1000;
                            return 0;
                        },
                    },
                ],
            },
            onNodeChange: (target) => {
                const position = (target as typeof target & { pos?: number }).pos;
                hoveredHandlePosition.value = typeof position === 'number' && position >= 0 ? position : null;
                const resolved = hoveredHandlePosition.value === null ? null : target.editor.state.doc.resolve(hoveredHandlePosition.value);
                const inTable = resolved ? Array.from({ length: resolved.depth + 1 }, (_, depth) => resolved.node(depth).type.name).includes('table') : false;
                dragHandleElement?.classList.toggle('vuey-editor-drag-handle--table', inTable || target.node?.type.name === 'table');
            },
            render: () => {
                const handle = document.createElement('div');
                dragHandleElement = handle;
                handle.className = 'vuey-editor-drag-handle';
                handle.setAttribute('title', 'Drag to move block');
                handle.setAttribute('aria-label', 'Drag to move block');
                handle.textContent = '⋮⋮';
                handle.addEventListener('click', event => {
                    event.preventDefault();
                    const currentEditor = editor.value;
                    if (!currentEditor) return;
                    openBlockMenu(event.clientX, event.clientY, hoveredHandlePosition.value ?? currentEditor.state.selection.from);
                });
                return handle;
            },
        }),
        ...(props.contractMode ? [ContractFieldNode, ContractSignatureNode, ContractTableNode] : []),
    ],
    editorProps: {
        attributes: {
            class: 'wysiwyg-editor px-5 py-4 text-sm leading-7 text-foreground outline-none',
        },
        handlePaste: (_view, event) => {
            const imageFiles = Array.from(event.clipboardData?.files || [])
                .filter((file) => file.type.startsWith('image/'));
            if (imageFiles.length === 0) return false;

            event.preventDefault();
            emit('pasteFiles', imageFiles);
            return true;
        },
    },
    onUpdate: ({ editor: currentEditor }) => {
        emit('update:modelValue', cleanupHtml(currentEditor.getHTML()));
    },
    onSelectionUpdate: ({ editor: currentEditor }) => {
        tableSelectionActive.value = currentEditor.isActive('table');
    },
    onBlur: () => {
        emit('blur');
    },
});

function openBlockMenu(x: number, y: number, position: number) {
    const currentEditor = editor.value;
    if (!currentEditor) return;
    if (currentEditor.state.doc.nodeAt(position)?.isBlock) {
        blockPosition.value = position;
    } else {
    const resolved = currentEditor.state.doc.resolve(Math.min(position, currentEditor.state.doc.content.size));
    const inColumns = resolved.depth > 1 && resolved.node(1).type.name === 'editorColumns';
    const depth = inColumns ? Math.min(3, resolved.depth) : 1;
    blockPosition.value = resolved.depth >= depth ? resolved.before(depth) : 0;
    }
    const selectedNode = currentEditor.state.doc.nodeAt(blockPosition.value);
    const isColumn = selectedNode?.type.name === 'editorColumn';
    activeBlockMenuItems.value = isColumn ? columnMenuItems : blockMenuItems;
    blockMenuTitle.value = isColumn ? 'Column actions' : 'Block actions';
    blockMenuLeft.value = Math.max(8, Math.min(x, window.innerWidth - 296));
    blockMenuTop.value = Math.max(8, Math.min(y, window.innerHeight - 336));
    blockMenuOpen.value = true;
    slashOpen.value = false;
}

function onEditorContextMenu(event: MouseEvent) {
    const currentEditor = editor.value;
    if (!currentEditor) return;
    const position = currentEditor.view.posAtCoords({ left: event.clientX, top: event.clientY });
    if (!position) return;
    event.preventDefault();
    openBlockMenu(event.clientX, event.clientY, position.pos);
}

function runBlockAction(id: string) {
    const currentEditor = editor.value;
    const position = blockPosition.value;
    blockMenuOpen.value = false;
    if (!currentEditor || position === null) return;
    const block = currentEditor.state.doc.nodeAt(position);
    if (!block) return;
    if (id === 'move-column-left' || id === 'move-column-right') {
        const resolved = currentEditor.state.doc.resolve(position);
        const columns = resolved.parent;
        if (columns.type.name !== 'editorColumns') return;
        const index = resolved.index();
        const otherIndex = index + (id === 'move-column-left' ? -1 : 1);
        if (otherIndex < 0 || otherIndex >= columns.childCount) return;
        const reordered = Array.from({ length: columns.childCount }, (_, childIndex) => columns.child(childIndex));
        [reordered[index], reordered[otherIndex]] = [reordered[otherIndex]!, reordered[index]!];
        const parentPosition = resolved.before();
        currentEditor.view.dispatch(currentEditor.state.tr.replaceWith(parentPosition, parentPosition + columns.nodeSize, columns.type.create(columns.attrs, reordered)));
        return;
    }
    if (id === 'duplicate') {
        currentEditor.commands.insertContentAt(position + block.nodeSize, block.toJSON());
        return;
    }
    if (id === 'delete') {
        currentEditor.commands.deleteRange({ from: position, to: position + block.nodeSize });
        return;
    }
    currentEditor.commands.setTextSelection(Math.min(position + 1, currentEditor.state.doc.content.size));
    const chain = currentEditor.chain().focus();
    if (id === 'image') { imageDraft.value = ''; imageEditorOpen.value = true; return; }
    if (id === 'table') { chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); return; }
    if (id === 'paragraph') chain.setParagraph().run();
    else if (id === 'heading-1') chain.setHeading({ level: 1 }).run();
    else if (id === 'heading-2') chain.setHeading({ level: 2 }).run();
    else if (id === 'heading-3') chain.setHeading({ level: 3 }).run();
    else if (id === 'bullet-list') chain.toggleBulletList().run();
    else if (id === 'ordered-list') chain.toggleOrderedList().run();
    else if (id === 'quote') chain.toggleBlockquote().run();
}

function insertImage() {
    const url = imageDraft.value.trim();
    if (!/^https:\/\//i.test(url)) return;
    editor.value?.chain().focus().setImage({ src: url }).run();
    imageEditorOpen.value = false;
    imageDraft.value = '';
}

function insertContractField(key: string, label: string) {
    editor.value?.chain().focus().insertContent({ type: 'contractField', attrs: { key, label } }).run();
}

function insertContractSignature(role: 'tenant' | 'client') {
    editor.value?.chain().focus().insertContent({ type: 'contractSignature', attrs: { role } }).run();
}

function insertContractTable(key: string, label: string) {
    editor.value?.chain().focus().insertContent({ type: 'contractTable', attrs: { key, label } }).run();
}

defineExpose({ insertContractField, insertContractSignature, insertContractTable });

const selectedTextStyle = computed({
    get: () => {
        if (!editor.value) return 'paragraph';
        if (editor.value.isActive('blockquote')) return 'blockquote';
        if (editor.value.isActive('heading', { level: 1 })) return 'heading-1';
        if (editor.value.isActive('heading', { level: 2 })) return 'heading-2';
        if (editor.value.isActive('heading', { level: 3 })) return 'heading-3';
        return 'paragraph';
    },
    set: (value: string) => {
        if (!editor.value) return;
        if (value === 'blockquote') {
            editor.value.chain().focus().toggleBlockquote().run();
            return;
        }
        if (value === 'heading-1') {
            editor.value.chain().focus().setHeading({ level: 1 }).run();
            return;
        }
        if (value === 'heading-2') {
            editor.value.chain().focus().setHeading({ level: 2 }).run();
            return;
        }
        if (value === 'heading-3') {
            editor.value.chain().focus().setHeading({ level: 3 }).run();
            return;
        }
        editor.value.chain().focus().setParagraph().run();
    },
});

watch(
    () => props.modelValue,
    (value) => {
        const currentEditor = editor.value;
        if (!currentEditor) return;

        const incoming = normalizeIncomingHtml(value);
        const current = cleanupHtml(currentEditor.getHTML());

        if (current !== incoming) {
            currentEditor.commands.setContent(incoming, { emitUpdate: false });
        }
    },
);

onBeforeUnmount(() => {
    editor.value?.destroy();
});

function normalizeIncomingHtml(value: string): string {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) return text;
    return text
        .split(/\n{2,}/)
        .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
        .join('');
}

function cleanupHtml(html: string): string {
    const normalized = String(html || '')
        .replace(/<p><\/p>/gi, '')
        .replace(/<p>\s*<\/p>/gi, '')
        .trim();

    return stripHtml(normalized).trim() || /<(?:hr|img|table|div\s+[^>]*(?:data-contract-signature|data-editor-columns)=)/i.test(normalized) ? normalized : '';
}

function stripHtml(value: string): string {
    return String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
}

function escapeHtml(text: string): string {
    const map: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        '\'': '&#039;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
}

function toggleBold() {
    editor.value?.chain().focus().toggleBold().run();
}

function toggleItalic() {
    editor.value?.chain().focus().toggleItalic().run();
}

function toggleUnderline() {
    editor.value?.chain().focus().toggleUnderline().run();
}

function toggleBulletList() {
    editor.value?.chain().focus().toggleBulletList().run();
}

function toggleOrderedList() {
    editor.value?.chain().focus().toggleOrderedList().run();
}

function undo() {
    editor.value?.chain().focus().undo().run();
}

function redo() {
    editor.value?.chain().focus().redo().run();
}

function setLink() {
    const currentEditor = editor.value;
    if (!currentEditor) return;
    linkDraft.value = currentEditor.getAttributes('link').href || 'https://';
    linkEditorOpen.value = true;
    nextTick(() => linkInput.value?.focus());
}

function setTextAlign(alignment: 'left' | 'center' | 'right' | 'justify') {
    editor.value?.chain().focus().setTextAlign(alignment).run();
}

function closeLinkEditor() {
    linkEditorOpen.value = false;
}

function saveLink() {
    const currentEditor = editor.value;
    if (!currentEditor) return;

    const url = linkDraft.value.trim();
    if (!url) {
        currentEditor.chain().focus().extendMarkRange('link').unsetLink().run();
        closeLinkEditor();
        return;
    }

    currentEditor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    closeLinkEditor();
}

function removeLink() {
    const currentEditor = editor.value;
    if (!currentEditor) return;
    currentEditor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLinkEditor();
}

function handleToolbarPointerDown(event: MouseEvent) {
    event.preventDefault();
}

function toolbarButtonClass(active: boolean) {
    return [
        'size-8 rounded-full transition-colors',
        active
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    ];
}

function alignmentButtonClass(alignment: 'left' | 'center' | 'right' | 'justify') {
    return toolbarButtonClass(editor.value?.isActive({ textAlign: alignment }) ?? false);
}

function runTableCommand(command: 'addRowBefore' | 'addRowAfter' | 'addColumnBefore' | 'addColumnAfter') {
    const chain = editor.value?.chain().focus();
    if (!chain) return;

    if (command === 'addRowBefore') chain.addRowBefore().run();
    else if (command === 'addRowAfter') chain.addRowAfter().run();
    else if (command === 'addColumnBefore') chain.addColumnBefore().run();
    else chain.addColumnAfter().run();
}
</script>

<template>
    <div class="space-y-3">
        <div v-if="showToolbar" class="flex flex-wrap items-center justify-between gap-3 rounded-[1.4rem] border border-border/70 bg-background/88 px-3.5 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
            <div class="flex flex-wrap items-center gap-2">
                <label class="relative">
                    <select
                        v-model="selectedTextStyle"
                        class="h-9 appearance-none rounded-full border border-border/70 bg-card/94 pl-3 pr-9 text-sm text-foreground shadow-sm outline-none transition-colors hover:border-border focus-visible:ring-2 focus-visible:ring-ring/50"
                    >
                        <option
                            v-for="option in textStyleOptions"
                            :key="option.value"
                            :value="option.value"
                        >
                            {{ option.label }}
                        </option>
                    </select>
                    <ChevronDown class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                </label>

                <div class="flex flex-wrap items-center gap-1 rounded-full border border-border/70 bg-card/94 p-1 shadow-sm">
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('bold') ?? false)" @mousedown="handleToolbarPointerDown" @click="toggleBold">
                        <Bold class="size-4" />
                        <span class="sr-only">Bold</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('italic') ?? false)" @mousedown="handleToolbarPointerDown" @click="toggleItalic">
                        <Italic class="size-4" />
                        <span class="sr-only">Italic</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('underline') ?? false)" @mousedown="handleToolbarPointerDown" @click="toggleUnderline">
                        <UnderlineIcon class="size-4" />
                        <span class="sr-only">Underline</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('heading', { level: 1 }) ?? false)" @mousedown="handleToolbarPointerDown" @click="selectedTextStyle = 'heading-1'">
                        <Heading1 class="size-4" />
                        <span class="sr-only">Heading 1</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('heading', { level: 2 }) ?? false)" @mousedown="handleToolbarPointerDown" @click="selectedTextStyle = 'heading-2'">
                        <Heading2 class="size-4" />
                        <span class="sr-only">Heading 2</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('heading', { level: 3 }) ?? false)" @mousedown="handleToolbarPointerDown" @click="selectedTextStyle = 'heading-3'">
                        <Heading3 class="size-4" />
                        <span class="sr-only">Heading 3</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('blockquote') ?? false)" @mousedown="handleToolbarPointerDown" @click="selectedTextStyle = 'blockquote'">
                        <Quote class="size-4" />
                        <span class="sr-only">Quote</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('bulletList') ?? false)" @mousedown="handleToolbarPointerDown" @click="toggleBulletList">
                        <List class="size-4" />
                        <span class="sr-only">Bullet list</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('orderedList') ?? false)" @mousedown="handleToolbarPointerDown" @click="toggleOrderedList">
                        <ListOrdered class="size-4" />
                        <span class="sr-only">Numbered list</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="alignmentButtonClass('left')" @mousedown="handleToolbarPointerDown" @click="setTextAlign('left')">
                        <AlignLeft class="size-4" />
                        <span class="sr-only">Align left</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="alignmentButtonClass('center')" @mousedown="handleToolbarPointerDown" @click="setTextAlign('center')">
                        <AlignCenter class="size-4" />
                        <span class="sr-only">Align center</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="alignmentButtonClass('right')" @mousedown="handleToolbarPointerDown" @click="setTextAlign('right')">
                        <AlignRight class="size-4" />
                        <span class="sr-only">Align right</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="alignmentButtonClass('justify')" @mousedown="handleToolbarPointerDown" @click="setTextAlign('justify')">
                        <AlignJustify class="size-4" />
                        <span class="sr-only">Justify</span>
                    </Button>
                    <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(editor?.isActive('link') ?? false)" @mousedown="handleToolbarPointerDown" @click="setLink">
                        <Link2 class="size-4" />
                        <span class="sr-only">Add link</span>
                    </Button>
                </div>
            </div>

            <div class="flex items-center gap-1 rounded-full border border-border/70 bg-card/94 p-1 shadow-sm">
                <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(false)" :disabled="!(editor?.can().undo())" @mousedown="handleToolbarPointerDown" @click="undo">
                    <RotateCcw class="size-4" />
                    <span class="sr-only">Undo</span>
                </Button>
                <Button type="button" variant="ghost" size="icon" :class="toolbarButtonClass(false)" :disabled="!(editor?.can().redo())" @mousedown="handleToolbarPointerDown" @click="redo">
                    <Redo2 class="size-4" />
                    <span class="sr-only">Redo</span>
                </Button>
            </div>
            <span class="hidden text-xs text-muted-foreground sm:inline">Type / for blocks</span>
        </div>

        <div v-if="linkEditorOpen" class="flex flex-wrap items-center gap-2 rounded-[1.2rem] border border-border/70 bg-card/94 px-3.5 py-3 shadow-sm">
            <Input
                ref="linkInput"
                v-model="linkDraft"
                placeholder="https://example.com"
                class="h-10 flex-1 min-w-[220px] rounded-xl bg-background/85"
                @keydown.enter.prevent="saveLink"
                @keydown.esc.prevent="closeLinkEditor"
            />
            <Button type="button" size="sm" class="rounded-full px-4" @click="saveLink">
                Apply link
            </Button>
            <Button type="button" variant="outline" size="sm" class="rounded-full px-4" @click="removeLink">
                Remove
            </Button>
            <Button type="button" variant="ghost" size="sm" class="rounded-full px-3" @click="closeLinkEditor">
                Cancel
            </Button>
        </div>

        <div v-if="imageEditorOpen" class="flex flex-wrap items-center gap-2 border-b border-border/50 py-2">
            <Input v-model="imageDraft" type="url" aria-label="Image URL" placeholder="https://example.com/image.jpg" class="min-w-[220px] flex-1" @keydown.enter.prevent="insertImage" @keydown.esc.prevent="imageEditorOpen = false" />
            <Button type="button" size="sm" :disabled="!/^https:\/\//i.test(imageDraft.trim())" @click="insertImage">Insert image</Button>
            <Button type="button" variant="ghost" size="sm" @click="imageEditorOpen = false">Cancel</Button>
        </div>

        <div
            class="wysiwyg-editor-shell"
            :class="{ 'wysiwyg-editor-shell--compact': density === 'compact' }"
            @contextmenu="onEditorContextMenu"
            @click="blockMenuOpen = false"
        >
            <div v-if="tableSelectionActive" class="flex flex-wrap items-center gap-1 border-b border-border/50 py-2" role="toolbar" aria-label="Table actions">
                <span class="mr-1 text-xs text-muted-foreground">Table</span>
                <Button type="button" variant="outline" size="sm" @mousedown="handleToolbarPointerDown" @click="runTableCommand('addRowBefore')">Add row above</Button>
                <Button type="button" variant="outline" size="sm" @mousedown="handleToolbarPointerDown" @click="runTableCommand('addRowAfter')">Add row below</Button>
                <Button type="button" variant="outline" size="sm" @mousedown="handleToolbarPointerDown" @click="runTableCommand('addColumnBefore')">Add column left</Button>
                <Button type="button" variant="outline" size="sm" @mousedown="handleToolbarPointerDown" @click="runTableCommand('addColumnAfter')">Add column right</Button>
            </div>
            <ClientOnly>
                <EditorContent v-if="editor" :editor="editor" />
                <template #fallback>
                    <div :class="density === 'compact' ? 'min-h-[140px]' : 'min-h-[240px]'" class="px-6 py-5 text-sm leading-7 text-muted-foreground">
                        Loading editor…
                    </div>
                </template>
            </ClientOnly>
        </div>

        <ClientOnly>
            <BubbleMenu v-if="editor" :editor="editor" :options="{ placement: 'top' }">
                <div class="flex items-center gap-1 rounded-lg border border-border/80 bg-popover p-1 shadow-lg" aria-label="Format selected text">
                    <Button type="button" variant="ghost" size="icon" class="size-8" :aria-pressed="editor.isActive('bold')" aria-label="Bold" @mousedown="handleToolbarPointerDown" @click="toggleBold"><Bold class="size-4" /></Button>
                    <Button type="button" variant="ghost" size="icon" class="size-8" :aria-pressed="editor.isActive('italic')" aria-label="Italic" @mousedown="handleToolbarPointerDown" @click="toggleItalic"><Italic class="size-4" /></Button>
                    <Button type="button" variant="ghost" size="icon" class="size-8" :aria-pressed="editor.isActive('underline')" aria-label="Underline" @mousedown="handleToolbarPointerDown" @click="toggleUnderline"><UnderlineIcon class="size-4" /></Button>
                    <Button type="button" variant="ghost" size="icon" class="size-8" aria-label="Add link" @mousedown="handleToolbarPointerDown" @click="setLink"><Link2 class="size-4" /></Button>
                </div>
            </BubbleMenu>
        </ClientOnly>

        <EditorSlashMenu :open="slashOpen" :items="slashItems" :selected-index="slashSelectedIndex" :left="slashLeft" :top="slashTop" @select="runSlashCommand" @close="slashOpen = false" />
        <EditorSlashMenu :open="blockMenuOpen" :items="activeBlockMenuItems" :left="blockMenuLeft" :top="blockMenuTop" :title="blockMenuTitle" searchable @select="runBlockAction" @close="blockMenuOpen = false" />

        <div v-if="helperText" class="text-xs text-muted-foreground">
            {{ helperText }}
        </div>
    </div>
</template>

<style scoped>
:global(.vuey-editor-drag-handle) {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.3rem;
    height: 1.8rem;
    margin-right: 1.5rem;
    border: 0;
    background: transparent;
    color: color-mix(in srgb, var(--muted-foreground) 60%, transparent);
    font-size: 0.9rem;
    line-height: 1;
    letter-spacing: -0.28rem;
    padding-right: 0.28rem;
    cursor: grab;
    box-shadow: none;
}
:global(.vuey-editor-drag-handle:hover) { color: var(--muted-foreground); background: transparent; }
:global(.vuey-editor-drag-handle:active) { cursor: grabbing; }
:global(.vuey-editor-drag-handle--table::after) {
    content: '';
    position: absolute;
    left: 100%;
    top: -0.25rem;
    width: 1.75rem;
    height: 2.3rem;
}
.wysiwyg-editor-shell :deep(.ProseMirror) {
    min-height: 240px;
    padding: 1.5rem 2.5rem 1.6rem;
    font-size: var(--app-body-font-size);
    line-height: 1.7;
    letter-spacing: 0;
}

.wysiwyg-editor-shell--compact :deep(.ProseMirror) {
    min-height: 140px;
    padding: 1rem 2.1rem 1.1rem;
}

.wysiwyg-editor-shell :deep([data-editor-columns]) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
    margin: 1rem 0;
}
.wysiwyg-editor-shell :deep([data-editor-columns="3"]) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.wysiwyg-editor-shell :deep([data-editor-column]) { min-width: 0; min-height: 3rem; padding: 0.3rem 0; }
@media (max-width: 640px) {
    .wysiwyg-editor-shell :deep([data-editor-columns]),
    .wysiwyg-editor-shell :deep([data-editor-columns="3"]) { grid-template-columns: minmax(0, 1fr); gap: 0.5rem; }
}

.wysiwyg-editor-shell :deep(.ProseMirror:focus) {
    outline: none;
}

.wysiwyg-editor-shell :deep(.ProseMirror p) {
    margin: 0 0 0.9rem;
}

.wysiwyg-editor-shell :deep(.ProseMirror h2) {
    margin: 1.2rem 0 0.85rem;
    font-size: 1.35rem;
    line-height: 1.75rem;
    font-weight: 600;
}

.wysiwyg-editor-shell :deep(.ProseMirror h1) {
    margin: 1.35rem 0 0.9rem;
    font-size: 1.9rem;
    line-height: 2.2rem;
    font-weight: 700;
}

.wysiwyg-editor-shell :deep(.ProseMirror h3) {
    margin: 1rem 0 0.75rem;
    font-size: 1.12rem;
    line-height: 1.6rem;
    font-weight: 600;
}

.wysiwyg-editor-shell :deep(.ProseMirror ul),
.wysiwyg-editor-shell :deep(.ProseMirror ol) {
    margin: 0 0 1rem;
    padding-left: 1.45rem;
}

.wysiwyg-editor-shell :deep(.ProseMirror li) {
    margin: 0.2rem 0;
}

.wysiwyg-editor-shell :deep(.ProseMirror ul) {
    list-style: disc;
    list-style-position: outside;
}

.wysiwyg-editor-shell :deep(.ProseMirror ol) {
    list-style: decimal;
    list-style-position: outside;
}

.wysiwyg-editor-shell :deep(.ProseMirror a) {
    color: var(--primary);
    text-decoration: underline;
}

.wysiwyg-editor-shell :deep(.ProseMirror blockquote) {
    margin: 0 0 1rem;
    border-left: 3px solid color-mix(in srgb, var(--primary) 48%, transparent);
    padding-left: 1rem;
    color: color-mix(in srgb, var(--foreground) 82%, var(--muted-foreground) 18%);
}

.wysiwyg-editor-shell :deep(.ProseMirror hr) { margin: 1.5rem 0; border: 0; border-top: 1px solid var(--border); }
.wysiwyg-editor-shell :deep(.ProseMirror pre) { margin: 0 0 1rem; overflow-x: auto; border-radius: var(--radius); background: var(--muted); padding: 0.85rem 1rem; font-family: var(--font-mono); font-size: 0.85rem; line-height: 1.6; }

.wysiwyg-editor-shell :deep(.ProseMirror p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    color: color-mix(in srgb, var(--muted-foreground) 92%, transparent);
    float: left;
    height: 0;
    pointer-events: none;
}
.wysiwyg-editor-shell :deep(.ProseMirror p.is-empty::before) {
    content: attr(data-placeholder);
    color: color-mix(in srgb, var(--muted-foreground) 72%, transparent);
    float: left;
    height: 0;
    pointer-events: none;
}
.wysiwyg-editor-shell :deep(.ProseMirror img) { display: block; max-width: 100%; height: auto; margin: 1rem 0; border-radius: var(--radius); }
.wysiwyg-editor-shell :deep(.tableWrapper) { width: 100%; max-width: 100%; overflow-x: auto; }
.wysiwyg-editor-shell :deep(.ProseMirror table) { width: 100%; min-width: 100%; margin: 1rem 0; border-collapse: collapse; table-layout: fixed; }
.wysiwyg-editor-shell :deep(.ProseMirror th), .wysiwyg-editor-shell :deep(.ProseMirror td) { position: relative; min-width: 5rem; border: 1px solid var(--border); padding: 0.5rem 0.65rem; vertical-align: top; }
.wysiwyg-editor-shell :deep(.ProseMirror th) { background: var(--muted); font-weight: 600; }
.wysiwyg-editor-shell :deep(.column-resize-handle) { position: absolute; top: 0; right: -2px; bottom: -2px; z-index: 1; width: 4px; background: color-mix(in srgb, var(--primary) 45%, transparent); pointer-events: none; }
.wysiwyg-editor-shell :deep(.resize-cursor) { cursor: col-resize; }
</style>
